import { useState, useCallback, useRef } from "react";
import { synthesizeSpeech } from "@/services/api/speech";
import {
  isBrowserSpeechSynthesisSupported,
  resolveSpeechVoice,
  speakWithBrowser,
  stopBrowserSpeech,
} from "./browserSpeechSynthesis";
import {
  createSharedAudioContext,
  disposeAnalyserNode,
  playMp3BlobWithAnalyser,
  type WebAudioPlaybackSession,
} from "./audioPlayback";
import { shouldFallbackToBrowserSpeech } from "./speechApiErrors";

function isPlaybackStoppedError(err: unknown): boolean {
  return err instanceof Error && err.message === "Playback stopped.";
}

export function useTextToSpeech(textToSpeak: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioAnalyser, setAudioAnalyser] = useState<AnalyserNode | null>(null);
  const playingRef = useRef(false);
  const loadingRef = useRef(false);
  const userStoppedRef = useRef(false);
  const playbackGenerationRef = useRef(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const playbackSessionRef = useRef<WebAudioPlaybackSession | null>(null);

  const isActive = isLoading || isPlaying;

  const setLoading = useCallback((value: boolean) => {
    loadingRef.current = value;
    setIsLoading(value);
  }, []);

  const cleanupPlayback = useCallback(() => {
    disposeAnalyserNode(playbackSessionRef.current?.analyser ?? null);
    playbackSessionRef.current = null;
    setAudioAnalyser(null);
  }, []);

  const playWithBrowserFallback = useCallback(async () => {
    if (!isBrowserSpeechSynthesisSupported()) {
      throw new Error("Read aloud is not supported in this browser.");
    }
    const voice = await resolveSpeechVoice();
    await speakWithBrowser(textToSpeak, voice, () => userStoppedRef.current);
  }, [textToSpeak]);

  const play = useCallback(async () => {
    const text = textToSpeak.trim();
    if (!text) {
      setError("No text to read aloud.");
      return;
    }
    if (loadingRef.current || playingRef.current) return;

    // Unlock Web Audio synchronously while we still have the user gesture.
    audioContextRef.current = createSharedAudioContext(audioContextRef.current);

    userStoppedRef.current = false;
    const generation = playbackGenerationRef.current + 1;
    playbackGenerationRef.current = generation;

    setLoading(true);
    setError(null);

    try {
      let blob: Blob;
      try {
        blob = await synthesizeSpeech({ text });
      } catch (apiErr) {
        if (userStoppedRef.current || playbackGenerationRef.current !== generation) {
          return;
        }
        if (shouldFallbackToBrowserSpeech(apiErr)) {
          setLoading(false);
          playingRef.current = true;
          setIsPlaying(true);
          setAudioAnalyser(null);
          await playWithBrowserFallback();
          return;
        }
        throw apiErr;
      }

      if (userStoppedRef.current || playbackGenerationRef.current !== generation) {
        return;
      }
      if (blob.size === 0) {
        throw new Error("Speech synthesis returned empty audio.");
      }

      const context = audioContextRef.current;
      if (!context) {
        throw new Error("Web Audio is not supported in this browser.");
      }

      setLoading(false);
      playingRef.current = true;
      setIsPlaying(true);

      const session = await playMp3BlobWithAnalyser(blob, context);
      playbackSessionRef.current = session;
      setAudioAnalyser(session.analyser);
      await session.done;
    } catch (err) {
      if (
        userStoppedRef.current ||
        playbackGenerationRef.current !== generation ||
        isPlaybackStoppedError(err)
      ) {
        return;
      }
      console.error("Error playing speech:", err);
      setError(err instanceof Error ? err.message : "Could not play speech.");
    } finally {
      if (playbackGenerationRef.current === generation) {
        playingRef.current = false;
        setIsPlaying(false);
        setLoading(false);
        cleanupPlayback();
      }
    }
  }, [textToSpeak, setLoading, playWithBrowserFallback, cleanupPlayback]);

  const stop = useCallback(() => {
    userStoppedRef.current = true;
    playbackGenerationRef.current += 1;
    playbackSessionRef.current?.stop();
    cleanupPlayback();
    stopBrowserSpeech();
    playingRef.current = false;
    setIsPlaying(false);
    setLoading(false);
    setError(null);
  }, [cleanupPlayback, setLoading]);

  const toggle = useCallback(() => {
    if (isPlaying) {
      stop();
    } else {
      void play();
    }
  }, [isPlaying, stop, play]);

  return {
    isLoading,
    isPlaying,
    isActive,
    audioAnalyser,
    error,
    play,
    stop,
    toggle,
  };
}
